import {
  HIDDEN_BACK_SIDE_NAME,
  PRINTED_SIDE_CODE,
  PRINTED_SIDE_VALUE_CODES,
  SWAG_PRODUCT_D_N_NAME,
  S_COLOR_CODE,
  S_CO_BRANDING,
  S_CO_BRANDING_VALUES,
  S_INK_COLOR_CODE,
} from '../constants';
import * as THREE from 'three';
import { ProductSpec, Surface, useBundleStore } from '../hooks/bundle-store';

export const removeMaterial = ({
  node,
  isSemitransparent,
}: {
  node: any;
  isSemitransparent: boolean;
}) => {
  if (!node.userData.material) {
    node.userData.material = node.material?.map;
  }

  node.material.map = null;
  node.material.needsUpdate = true;
  // manage semitransparent sections
  if (isSemitransparent) {
    node.visible = false;
    node.needsUpdate = true;
  }
};

export const addMaterial = ({
  node,
  isSemitransparent,
}: {
  node: any;
  isSemitransparent: boolean;
}) => {
  if (node?.userData?.material) {
    node.material.map = node?.userData?.material;
    node.material.needsUpdate = true;
  }
  // manage semitransparent sections
  if (isSemitransparent) {
    node.visible = true;

    node.needsUpdate = true;
  }
};

export const applyTextureOnModel = ({
  isSemitransparent,
  texture,
  isTextureExist,
  isSwagProduct,
  node,
  isReset = false,
}: {
  isSemitransparent: boolean;
  texture: THREE.Texture;
  isTextureExist: boolean;
  isSwagProduct?: boolean;
  node: any;
  isReset?: boolean;
}) => {
  if (isSemitransparent) {
    node.geometry.computeVertexNormals();
    // var vertexShader = `
    // varying vec2 vUv;
    // varying vec3 vNormal;

    // void main() {
    //     vUv = uv; // Pass UV coordinates to fragment shader
    //     vNormal = normalize(normalMatrix * normal);
    //     gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    // }
    // `;

    // var fragmentShader = `
    // varying vec2 vUv; // Add varying for texture coordinates
    // varying vec3 vNormal;
    // uniform sampler2D textureMap;
    // uniform vec2 resolution; // Add resolution uniform

    // void main() {
    //     float dotProduct = dot(vNormal, normalize(-vec3(0.0, 0.0, 1.0))); // Calculate dot product with camera direction
    //     float backFaceOpacity = 0.8; // Semi-transparent for back faces
    //     float frontFaceOpacity = 1.0; // Fully opaque for front faces

    //     float finalOpacity = dotProduct > 0.0 ? backFaceOpacity  : frontFaceOpacity ;

    //     vec4 texColor = texture2D(textureMap,vUv); // Sample texture color
    //     if (dotProduct > 0.0) {
    //       gl_FragColor = vec4(texColor.rgb, texColor.a * finalOpacity);
    //       return;
    //     }
    //     gl_FragColor=texColor;
    // }
    // `;

    // const canvasTexture = changeTextureToCanvasTexture(texture);
    // var uniforms = {
    //   textureMap: { value: canvasTexture }, // Texture to apply
    // };

    // const material = new THREE.ShaderMaterial({
    //   uniforms: uniforms,
    //   vertexShader: vertexShader,
    //   fragmentShader: fragmentShader,
    //   transparent: true,
    //   side: 2,
    // });
    // material.needsUpdate = true;

    // node.material = material;

    texture.colorSpace = THREE.SRGBColorSpace;
    const material = new THREE.MeshStandardMaterial({
      map: texture,
      transparent: true,
      toneMapped: false,
      side: 2,
    });
    material.onBeforeCompile = (shader) => {
      shader.uniforms.backFaceOpacity = { value: 0.9 };
      shader.uniforms.frontFaceOpacity = { value: 1.0 };
      // Add varying for backface detection
      shader.vertexShader = shader.vertexShader.replace(
        '#include <common>',
        `
    #include <common>
    varying vec3 vNormalWorld;
    `,
      );
      shader.vertexShader = shader.vertexShader.replace(
        '#include <begin_vertex>',
        `
    #include <begin_vertex>
    vNormalWorld = normalize(normalMatrix * normal);
    `,
      );
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <common>',
        `
    #include <common>
    varying vec3 vNormalWorld;
    uniform float backFaceOpacity;
    uniform float frontFaceOpacity;
    `,
      );
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <dithering_fragment>',
        `
    float dotProduct = dot(vNormalWorld, normalize(-vec3(0.0, 0.0, 1.0)));
    float finalOpacity = dotProduct > 0.0 ? backFaceOpacity : frontFaceOpacity;
    gl_FragColor.a *= finalOpacity;
    #include <dithering_fragment>
    `,
      );
    };
    texture.flipY = false;
    material.needsUpdate = true;
    node.material = material;
    if (isReset) {
      node.userData.material = material?.map;
    }
  } else {
    if (isTextureExist) {
      texture.flipY = false;
      texture.colorSpace = THREE.SRGBColorSpace;
    }

    const newMaterial = new THREE.MeshBasicMaterial({
      map: texture,
      toneMapped: false,
      transparent: isSwagProduct,
    });

    node.material = newMaterial;
    const sectionNode = node?.parent;
    const duplicateSectionNode = sectionNode.getObjectByName(
      sectionNode?.userData?.name?.toLowerCase(),
    );

    if (duplicateSectionNode) {
      const duplicateSurfaceNode = node?.parent?.getObjectByName(
        node?.userData?.name?.toLowerCase(),
      );
      duplicateSurfaceNode.material = newMaterial;
    }
  }
};

export const update3dSurfaceAndSectionCodes = ({
  model,
  sectionCode,
  sectionId,
  surfaces,
  isSemitransparent,
  surfaceIds,
  specs,
  isSwagProduct,
  brandTextures,
  modelId,
  isReset,
}: {
  sectionId: string;
  sectionCode: string;
  surfaces: Surface[];
  model: THREE.Mesh;
  isSemitransparent: boolean;
  surfaceIds?: string[];
  specs: ProductSpec[];
  isSwagProduct?: boolean;
  brandTextures?: THREE.Texture[];
  modelId: number;
  isReset: boolean;
}) => {
  const node = model?.getObjectByName(isReset ? sectionId : sectionCode);
  if (node && !isReset) {
    node.name = sectionId;
  }

  //manage Swag products
  if (isSwagProduct) {
    const designableNode = node?.children?.find((item) => item?.name === SWAG_PRODUCT_D_N_NAME);
    if (designableNode) {
      const colorSpec = specs?.find((sp) => sp?.code === S_COLOR_CODE);
      if (colorSpec) {
        const specValueCode = colorSpec?.specValue?.code;
        const color = '#' + specValueCode;

        const mColor = new THREE.Color(color);
        //@ts-expect-error
        designableNode.material.color = mColor;
        //@ts-expect-error
        designableNode.needsUpdate = true;
      }
    }
  }

  let isFirstSurfaceHaveHideBackSide = false;
  surfaces?.forEach((surface, index) => {
    const id = surface?.id;
    const node = model?.getObjectByName(isReset ? id : surface?.code);

    if (node) {
      if (!isReset) {
        node.name = id;
      }
      const isTextureExist = Boolean(surface?.texture);
      //@ts-expect-error
      let texture = isTextureExist ? surface?.texture : node?.material?.map;

      const coBrandingSpec = specs?.find((s) => s.code === S_CO_BRANDING);
      if (coBrandingSpec) {
        const code = coBrandingSpec?.specValue?.code;
        if (code === S_CO_BRANDING_VALUES[0]) {
          const newTexture = brandTextures?.[index];
          if (newTexture) {
            texture = newTexture;
          }
        }
      }

      if (texture) {
        applyTextureOnModel({
          isSemitransparent,
          isSwagProduct,
          isTextureExist,
          texture,
          node,
          isReset,
        });

        //manage ink color specs
        if (isSwagProduct) {
          const inkColorSpec = specs?.find((sp) => sp?.code === S_INK_COLOR_CODE);
          if (inkColorSpec) {
            const specValueCode = inkColorSpec?.specValue?.code;
            const color = '#' + specValueCode;
            //@ts-expect-error
            updateImageTextsColor(texture.image, color, node.material);
            node.userData.texture = texture;
          }
        }

        //init hidden backsides
        const childName = node?.children?.[0]?.name;
        const outsideKey = PRINTED_SIDE_VALUE_CODES[1];
        if ((index === 0 || isFirstSurfaceHaveHideBackSide) && specs && node) {
          if (childName?.includes(HIDDEN_BACK_SIDE_NAME)) {
            if (isFirstSurfaceHaveHideBackSide) {
              useBundleStore.getState().setDiffSectionDesigns(modelId, sectionId);
            }
            isFirstSurfaceHaveHideBackSide = true;

            const printedSideSpec = specs?.find((s) => s.code === PRINTED_SIDE_CODE);
            const code = printedSideSpec?.specValue?.code;
            const childNode = node?.children?.[0];
            applyTextureOnModel({
              isSemitransparent,
              isSwagProduct,
              isTextureExist,
              texture,
              node: childNode,
              isReset,
            });

            if (code != PRINTED_SIDE_VALUE_CODES[2]) {
              const isOutAsSingleSide = code === outsideKey;

              removeMaterial({
                node: isOutAsSingleSide ? node : childNode,
                isSemitransparent: isSemitransparent,
              });
            }
          }
        }

        if (
          !surfaceIds?.includes(id) &&
          sectionCode !== 'CANOPY' &&
          !isFirstSurfaceHaveHideBackSide
        ) {
          removeMaterial({ node, isSemitransparent });
          return;
        }
      } else {
        if (isReset) {
          //@ts-ignore
          node?.material?.dispose();
          //@ts-ignore
          node.material = new THREE.MeshBasicMaterial({ color: 0xffffff }); // or null
          //@ts-ignore
          node.needsUpdate = true;
        }
      }
    }
  });
};

const changeTextureToCanvasTexture = (texture: THREE.MeshBasicMaterial['map']) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  // Set canvas size to match texture size
  canvas.width = texture?.image.width;
  canvas.height = texture?.image.height;

  // Draw the texture onto the canvas
  context?.drawImage(texture?.image, 0, 0);

  // Create a CanvasTexture from the canvas
  const canvasTexture = new THREE.CanvasTexture(canvas);

  canvasTexture.flipY = false;

  return canvasTexture;
};

export function updateImageTextsColor(
  image: any,
  newColor: string,
  material: THREE.MeshBasicMaterial,
) {
  // Create a canvas
  const canvas = document.createElement('canvas');
  canvas.width = image.width;
  canvas.height = image.height;
  const context = canvas.getContext('2d') as CanvasRenderingContext2D;

  // Draw the image onto the canvas
  context.drawImage(image, 0, 0);

  // Get image data
  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  // Define the new color
  const [rNew, gNew, bNew] = hexToRgb(newColor);

  // Loop through all pixels
  for (let i = 0; i < data.length; i += 4) {
    const [r, g, b] = [data[i], data[i + 1], data[i + 2]];

    // Assume the text color is white and change it
    // You may need more complex logic to detect text pixels
    if (r > 200 && g > 200 && b > 200) {
      data[i] = rNew;
      data[i + 1] = gNew;
      data[i + 2] = bNew;
    }
  }

  // Put the modified data back to the canvas
  context.putImageData(imageData, 0, 0);

  // Create a new texture from the canvas
  const newTexture = new THREE.CanvasTexture(canvas);
  newTexture.flipY = false;
  // Update the material
  material.map = newTexture;
  material.needsUpdate = true;
}

function hexToRgb(hex: string) {
  hex = hex.replace('#', '');
  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return [r, g, b];
}
